import React, { useEffect, useState } from 'react';
import Swal from '../librarianSwal';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import './ReportDashboard.css';
import '../lib-page-header.css';

import {
  getBooksByLibrary,
  getBookCopiesByLibrary,
  getReservationsByLibrary,
  getIssuesByLibrary,
  getFinesByLibrary,
  getPaymentsByLibrary,
  getMembersByLibrary,
  getStudentsByLibrary,
  getErrorMessage
} from '../../api/api';

const ReportDashboard = () => {
  const { user } = useAuth();
  const libraryId = useActiveLibraryId();

  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [report, setReport] = useState({
    books: [],
    copies: [],
    reservations: [],
    issues: [],
    fines: [],
    payments: [],
    members: [],
    students: []
  });

  const fetchReports = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);

      const [
        books,
        copies,
        reservations,
        issues,
        fines,
        payments,
        members,
        students
      ] = await Promise.all([
        getBooksByLibrary(libraryId),
        getBookCopiesByLibrary(libraryId),
        getReservationsByLibrary(libraryId),
        getIssuesByLibrary(libraryId),
        getFinesByLibrary(libraryId),
        getPaymentsByLibrary(libraryId),
        getMembersByLibrary(libraryId),
        getStudentsByLibrary(libraryId)
      ]);

      setReport({
        books: books || [],
        copies: copies || [],
        reservations: reservations || [],
        issues: issues || [],
        fines: fines || [],
        payments: payments || [],
        members: members || [],
        students: students || []
      });
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const totalPayment = report.payments.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  const totalFine = report.fines.reduce(
    (sum, item) => sum + Number(item.amount || item.fine_amount || 0),
    0
  );

  const unpaidFine = report.fines
    .filter((item) => (item.fine_status || item.status) === 'unpaid')
    .reduce((sum, item) => sum + Number(item.balance_amount || item.amount || 0), 0);

  const Card = ({ title, value, accent, icon }) => (
    <div className="report-card" style={{ '--accent': accent }}>
      {icon && (
        <span
          className="report-card__icon"
          style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}
        >
          <i className={icon} />
        </span>
      )}
      <span className="report-card__title">{title}</span>
      <span className="report-card__value">{value}</span>
    </div>
  );

  return (
    <div className="container-fluid mt-3">
      <style>
        {`
          @media print {
            @page { size: A4 portrait; margin: 12mm; }

            body * { visibility: hidden; }

            .report-print-area, .report-print-area * { visibility: visible; }

            .report-print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }

            .report-print-area .card-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}
      </style>

      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-chart-bar" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Reports</h3>
            <div className="lib-page-header__subtitle">Library summary and MIS reports</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button className="btn btn-primary" onClick={() => window.print()}>
            Print Report
          </button>
        </div>
      </div>

      {showLoader ? (
        <Loader />
      ) : (
        <>
          <div className="report-grid">
            <Card title="Books" value={report.books.length} accent="#6f8fe6" icon="fas fa-book" />
            <Card title="Book Copies" value={report.copies.length} accent="#7c5cfc" icon="fas fa-layer-group" />
            <Card
              title="Available Copies"
              value={report.copies.filter((item) => item.availability_status === 'available').length}
              accent="#22b07d"
              icon="fas fa-check-circle"
            />
            <Card
              title="Issued Copies"
              value={report.copies.filter((item) => item.availability_status === 'issued').length}
              accent="#f5a524"
              icon="fas fa-book-reader"
            />

            <Card title="Members" value={report.members.length} accent="#2dabb5" icon="fas fa-users" />
            <Card title="Students" value={report.students.length} accent="#5a7ce0" icon="fas fa-user-graduate" />
            <Card title="Parent Requests" value={report.reservations.length} accent="#d6669a" icon="fas fa-inbox" />
            <Card
              title="Pending Requests"
              value={report.reservations.filter((item) => item.status === 'requested').length}
              accent="#f59e0b"
              icon="fas fa-hourglass-half"
            />

            <Card title="Issues" value={report.issues.length} accent="#6366f1" icon="fas fa-exchange-alt" />
            <Card
              title="Active Issues"
              value={report.issues.filter((item) => item.issue_status === 'issued').length}
              accent="#0ea5e9"
              icon="fas fa-hand-holding"
            />
            <Card title="Total Fine" value={`₹ ${totalFine}`} accent="#ef5350" icon="fas fa-coins" />
            <Card title="Unpaid Fine" value={`₹ ${unpaidFine}`} accent="#f97362" icon="fas fa-exclamation-triangle" />
            <Card title="Total Payments" value={`₹ ${totalPayment}`} accent="#16a34a" icon="fas fa-money-bill-wave" />
          </div>

          <div className="card shadow-sm mt-3 table-full-card report-print-area">
            <div className="card-header fw-bold"><i className="fas fa-chart-pie me-2" />Quick Summary</div>

            <div className="card-body">
              <div className="d-none d-print-block text-center mb-3">
                <div style={{ fontWeight: 700, fontSize: 14 }}>Community Library Management System</div>
                <div style={{ fontSize: 12 }}>Library Summary Report</div>
                <div className="small text-muted">Generated: {new Date().toLocaleString('en-IN')}</div>
              </div>
              <div className="table-responsive">
                <table className="table table-bordered align-middle">
                  <tbody>
                    <tr>
                      <th>Total Books</th>
                      <td>{report.books.length}</td>
                      <th>Total Copies</th>
                      <td>{report.copies.length}</td>
                    </tr>

                    <tr>
                      <th>Available Copies</th>
                      <td>{report.copies.filter((item) => item.availability_status === 'available').length}</td>
                      <th>Issued Copies</th>
                      <td>{report.copies.filter((item) => item.availability_status === 'issued').length}</td>
                    </tr>

                    <tr>
                      <th>Total Members</th>
                      <td>{report.members.length}</td>
                      <th>Total Students</th>
                      <td>{report.students.length}</td>
                    </tr>

                    <tr>
                      <th>Total Fine</th>
                      <td>₹ {totalFine}</td>
                      <th>Unpaid Fine</th>
                      <td>₹ {unpaidFine}</td>
                    </tr>

                    <tr>
                      <th>Total Payments</th>
                      <td>₹ {totalPayment}</td>
                      <th>Total Parent Requests</th>
                      <td>{report.reservations.length}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportDashboard;